// mealPlanHelpers.js — Pure utility functions extracted from MealPlan.js
// NOTE: This file must stay free of React imports so it can be unit-tested in Node.
import { getCountryCurrency } from "../../../services/countryService";
import { isFishIngredientName, isFishProductItem, mergeStorageDataIntoItem } from "../../../utils/recipeFreshnessRules";

/** Default recipe count (matches TOTAL_EXPECTED_RECIPES). */
const DEFAULT_RECIPE_COUNT = 9;

/**
 * Generate placeholder recipe objects for the loading UI
 * @param {Function} t - i18n translation function
 * @param {number} [count=9] - Number of placeholders to create
 * @returns {Array} Array of placeholder recipe objects
 */
export function generatePlaceholderRecipes(t, count = DEFAULT_RECIPE_COUNT) {
  const placeholders = [];
  for (let i = 0; i < count; i++) {
    placeholders.push({
      id: `placeholder-${i}`,
      title: t('loading.title'),
      description: t('loading.preparing'),
      isPlaceholder: true,
      mealType: i < 3 ? "Breakfast" : i < 6 ? "Lunch" : "Dinner",
    });
  }
  return placeholders;
}

/**
 * Recursively count all products across category groups
 * @param {Array} list - Product list (possibly nested)
 * @returns {number} Total number of product items
 */
export function countAllProducts(list) {
  if (!Array.isArray(list)) return 0;
  return list.reduce((sum, group) => {
    if (Array.isArray(group.items)) {
      if (
        group.items.length > 0 &&
        group.items[0].items &&
        Array.isArray(group.items[0].items)
      ) {
        return sum + countAllProducts(group.items);
      }
      return sum + group.items.length;
    }
    return sum;
  }, 0);
}

/**
 * Compute total cost from productList items directly
 * @param {Array} productList - Array of product categories
 * @returns {number} Computed total cost
 */
export function computeTotalFromProducts(productList) {
  if (!productList || productList.length === 0) return 0;
  return productList.reduce((sum, cat) => {
    return sum + (cat.items || []).reduce((c, item) => {
      if (item.unmatched === true || item.error) return c;
      const num = parseFloat((item.totalPrice || '').toString().replace(/[^0-9.-]/g, ''));
      return c + (isNaN(num) ? 0 : num);
    }, 0);
  }, 0);
}

/**
 * Compute adjusted total from productList excluding "I have it" items.
 * Returns null if no haveIt items are active (no adjustment needed).
 * @param {Array} productList - Array of product categories
 * @param {Object} haveItState - Map of productName → boolean
 * @returns {number|null} Adjusted total or null if no adjustment
 */
export function computeAdjustedTotalFromProducts(productList, haveItState) {
  if (!productList || productList.length === 0) return null;
  if (!haveItState || !Object.values(haveItState).some(Boolean)) return null;

  return productList.reduce((sum, cat) => {
    return sum + (cat.items || []).reduce((c, item) => {
      if (item.unmatched === true || item.error) return c;
      if (haveItState[item.productName]) return c;
      const num = parseFloat((item.totalPrice || '').toString().replace(/[^0-9.-]/g, ''));
      return c + (isNaN(num) ? 0 : num);
    }, 0);
  }, 0);
}

/**
 * Compute consumed-only cost per recipe from the productList, excluding any
 * product the user marked as "I have it". Each product carries a
 * `recipeCosts: [{ title, cost }]` breakdown produced by the grocery processor;
 * this sums those by recipe title so each meal card can show its own cost.
 * Recomputes live as haveItState changes (mirrors computeAdjustedTotalFromProducts).
 * @param {Array} productList - Array of product categories
 * @param {Object} haveItState - Map of productName → boolean
 * @returns {Object} Map of recipe title → total cost (number)
 */
export function computeRecipeCostsFromProducts(productList, haveItState) {
  const costByTitle = {};
  if (!productList || productList.length === 0) return costByTitle;

  productList.forEach((cat) => {
    (cat.items || []).forEach((item) => {
      if (item.unmatched === true || item.error) return;
      if (haveItState && haveItState[item.productName]) return;
      if (!Array.isArray(item.recipeCosts)) return;
      item.recipeCosts.forEach(({ title, cost }) => {
        if (!title || !(cost > 0)) return;
        costByTitle[title] = (costByTitle[title] || 0) + cost;
      });
    });
  });

  return costByTitle;
}

/**
 * Get the display cost string with proper currency formatting
 * @param {Object} params
 * @param {string|null} params.adjustedTotalCost
 * @param {string} params.totalCost
 * @param {number} params.computedTotalFromProducts
 * @param {string|null} params.userCountry
 * @returns {string} Formatted cost string
 */
export function getDisplayCost({ adjustedTotalCost, totalCost, computedTotalFromProducts, userCountry }) {
  const currencySymbol = userCountry ? getCountryCurrency(userCountry).symbol : "€";
  const defaultCost = `0${currencySymbol}`;

  if (adjustedTotalCost && adjustedTotalCost !== "") return adjustedTotalCost;
  if (totalCost && totalCost !== "") return totalCost;
  if (computedTotalFromProducts > 0) {
    return `${currencySymbol}${computedTotalFromProducts.toFixed(2)}`;
  }
  return defaultCost;
}

/**
 * Build a storage map from productList + recipe instructions for storage data preservation.
 * Used when replacing recipes to preserve storage/shelfLifeDays across grocery list regeneration.
 * @param {Array} productList - Current product list
 * @param {Array} mealPlan - Current meal plan recipes
 * @returns {Map<string, {storage: string|null, shelfLifeDays: number|null}>}
 */
export function buildStorageMap(productList, mealPlan) {
  const savedStorageMap = new Map();

  if (productList && Array.isArray(productList)) {
    for (const cat of productList) {
      if (!cat.items) continue;
      for (const item of cat.items) {
        if (item.storage || (item.shelfLifeDays && item.shelfLifeDays > 0)) {
          const key = (item.productName || item.name || '').toLowerCase().trim();
          if (key) {
            savedStorageMap.set(key, {
              storage: item.storage || null,
              shelfLifeDays: item.shelfLifeDays > 0 ? item.shelfLifeDays : null,
            });
          }
          const origKey = (item.originalName || '').toLowerCase().trim();
          if (origKey && origKey !== key) {
            savedStorageMap.set(origKey, {
              storage: item.storage || null,
              shelfLifeDays: item.shelfLifeDays > 0 ? item.shelfLifeDays : null,
            });
          }
        }
      }
    }
  }

  // Collect ingredient_storage from all recipes' instructions
  if (mealPlan) {
    for (const recipe of mealPlan) {
      const storage = recipe.instructions?.ingredient_storage;
      if (Array.isArray(storage)) {
        for (const entry of storage) {
          if (entry.name && entry.storage) {
            const key = entry.name.toLowerCase().trim();
            const days = Number(entry.shelf_life_days);
            if (!savedStorageMap.has(key) || (entry.storage === 'freezer' && isFishIngredientName(key))) {
              savedStorageMap.set(key, {
                storage: entry.storage,
                shelfLifeDays: days > 0 ? days : null,
              });
            }
          }
        }
      }
    }
  }

  return savedStorageMap;
}

/**
 * Create an enrichment function that applies storage data to product items.
 * @param {Map} newRecipeStorageMap - Storage data from the new recipe's ingredient_storage
 * @param {Map} savedStorageMap - Storage data preserved from old productList + other recipes
 * @returns {Function} enrichItem(item) → enriched item
 */
export function createStorageEnricher(newRecipeStorageMap, savedStorageMap) {
  return (item) => {
    if (item.storage && item.shelfLifeDays > 0 && !isFishProductItem(item)) return item;

    const productName = (item.productName || item.name || '').toLowerCase().trim();
    const originalName = (item.originalName || '').toLowerCase().trim();

    // Try matching from new recipe's ingredient_storage first
    let storageData = null;
    for (const [ingName, data] of newRecipeStorageMap) {
      if (productName.includes(ingName) || ingName.includes(productName) ||
          (originalName && (originalName.includes(ingName) || ingName.includes(originalName)))) {
        storageData = data;
        break;
      }
    }

    // Fallback: try matching from savedStorageMap
    if (!storageData) {
      storageData = savedStorageMap.get(productName) || null;
      if (!storageData && originalName) {
        storageData = savedStorageMap.get(originalName) || null;
      }
      if (!storageData) {
        for (const [savedKey, data] of savedStorageMap) {
          if (productName.includes(savedKey) || savedKey.includes(productName) ||
              (originalName && (originalName.includes(savedKey) || savedKey.includes(originalName)))) {
            storageData = data;
            break;
          }
        }
      }
    }

    if (storageData) {
      return mergeStorageDataIntoItem(item, storageData);
    }
    return item;
  };
}

/**
 * Determine servingsForDays based on cooking style
 * @param {Object} cookingStyle - Full cooking style object
 * @param {string} mealType - Recipe meal type (Breakfast, Lunch, Dinner)
 * @returns {number} 1 or 2
 */
export function getServingsForDays(cookingStyle, mealType) {
  const mealTypeKey = mealType?.toLowerCase() || '';
  const mealTypeStyle = cookingStyle?.[mealTypeKey];
  return typeof mealTypeStyle === 'object'
    ? (mealTypeStyle?.cookFor2Days ? 2 : 1)
    : (mealTypeStyle || 1);
}

/**
 * Check if a cooking style entry means "cook for 2 days"
 * @param {Object} cookingStyle
 * @param {string} mealType - lowercase meal type key
 * @returns {boolean}
 */
export function isCookFor2Days(cookingStyle, mealType) {
  const styleEntry = cookingStyle?.[mealType];
  return typeof styleEntry === 'object' ? !!styleEntry?.cookFor2Days : styleEntry === 2;
}
